import type { WithContext } from "schema-dts";

interface FaqItem {
  id: string;
  heading: string;
  body: string;
}

interface Question {
  "@type": "Question";
  name: string;
  acceptedAnswer: {
    "@type": "Answer";
    text: string;
  };
}

interface FAQPage {
  "@type": "FAQPage";
  mainEntity: Question[];
  inLanguage?: string;
}

/**
 * Strips HTML tags and converts newlines to spaces for schema
 */
function stripHtmlAndNormalizeText(text: string): string {
  // Remove HTML tags if any
  let cleaned = text.replace(/<[^>]*>/g, "");
  
  // Convert newlines to spaces
  cleaned = cleaned.replace(/\n+/g, " ");
  
  // Normalize multiple spaces to single space
  cleaned = cleaned.replace(/\s+/g, " ");
  
  // Trim whitespace
  return cleaned.trim();
}

/**
 * Generates FAQPage schema from FAQ items
 * 
 * @param items - Array of FAQ items with heading and body
 * @param locale - Language code for inLanguage property
 * @returns FAQPage schema object with @context
 */
export function generateFaqSchema(
  items: FaqItem[],
  locale: string = "en"
): WithContext<FAQPage> {
  const mainEntity: Question[] = items.map((item) => ({
    "@type": "Question",
    name: item.heading,
    acceptedAnswer: {
      "@type": "Answer",
      text: stripHtmlAndNormalizeText(item.body),
    },
  }));

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity,
    inLanguage: locale,
  };
}

